/**
 * Pricing helpers for template product cards (step 2) and the step-3 cart/header
 * when the API returns per-insurance `insuranceCompanies` on a booking template.
 *
 * Price-display rules by ambulance type:
 *
 * PLUS_DEFAULT_PRICE_FOR_INSURED
 *   - Step 2: show only the self-payer price, no "od" prefix.
 *   - Step 3: always show self-payer price everywhere immediately.
 *
 * DEFAULT | PLUS_WITH_AND_WITHOUT_YEAR_PROGRAM_PRICE
 *   - Step 2: show "Manažment návštevy" (cheapest of 3 SK insurers) and "Samoplatca"
 *             rows, both prefixed with "od".
 *   - Step 3 before insurance selected: show cheapest price across ALL insurers with "od".
 *   - Step 3 after insurer selected:
 *       SA00 → label "Cena", real self-payer price, no "od".
 *       any other → label "Manažment návštevy", real insurer price, no "od".
 */

export type TemplateListInsuranceRow = {
	insuranceCompany: { id?: number; name?: string; code?: string | null }
	price: number
	description?: string | null
}

/** Dôvera (2400), VšZP (2500), UNION (2700) — the three main Slovak public health insurers. */
const SK_MAIN_PUBLIC_INSURER_CODES = new Set(['2400', '2500', '2700'])

/** Code used for self-paying patients (no public insurance). */
export const SELF_PAID_INSURER_CODE = 'SA00'

function normalizeInsuranceCode(code: string | null | undefined): string {
	return String(code ?? '')
		.trim()
		.toUpperCase()
}

/**
 * Returns `true` when the given insurance company code belongs to a self-paying patient.
 * Used in step 3 to decide between "Cena" and "Manažment návštevy" labels.
 */
export function isSelfPaidInsuranceCode(code: string | null | undefined): boolean {
	return normalizeInsuranceCode(code) === SELF_PAID_INSURER_CODE
}

/**
 * Lowest price among the three main Slovak public insurers (Dôvera, VšZP, UNION).
 * Used on step-2 product cards for the "Manažment návštevy" row.
 * Returns `fallback` when none of the three insurers appear in the data.
 */
export function getCheapestSkMainInsurancePrice(
	insuranceCompanies: TemplateListInsuranceRow[] | undefined,
	fallback: number | null
): number | null {
	if (!insuranceCompanies?.length) return fallback

	const prices = insuranceCompanies
		.filter(ic => {
			const code = normalizeInsuranceCode(ic.insuranceCompany?.code)
			return code !== '' && SK_MAIN_PUBLIC_INSURER_CODES.has(code)
		})
		.map(ic => ic.price)
		.filter((p): p is number => typeof p === 'number' && !Number.isNaN(p) && p >= 0)

	if (prices.length === 0) return fallback
	return Math.min(...prices)
}

/**
 * Lowest price across ALL insurance companies (including EU variants and self-payer).
 * Used in the step-3 cart/header "od" label before the patient selects an insurer —
 * shows the absolute minimum they might pay.
 * Returns `fallback` when no valid prices exist.
 */
export function getCheapestAllInsurancePrice(
	insuranceCompanies: TemplateListInsuranceRow[] | undefined,
	fallback: number | null
): number | null {
	if (!insuranceCompanies?.length) return fallback

	const prices = insuranceCompanies
		.map(ic => ic.price)
		.filter((p): p is number => typeof p === 'number' && !Number.isNaN(p) && p >= 0)

	if (prices.length === 0) return fallback
	return Math.min(...prices)
}

/**
 * Price from the self-payer entry (SA00).
 * Used on step-2 product cards for the "Samoplatca" row, and for
 * PLUS_DEFAULT_PRICE_FOR_INSURED ambulances where all prices are fixed.
 * Returns `fallback` when no SA00 entry is present.
 */
export function getSelfPaidInsurancePrice(
	insuranceCompanies: TemplateListInsuranceRow[] | undefined,
	fallback: number | null
): number | null {
	if (!insuranceCompanies?.length) return fallback

	const entry = insuranceCompanies.find(
		ic => normalizeInsuranceCode(ic.insuranceCompany?.code) === SELF_PAID_INSURER_CODE
	)

	if (entry == null || typeof entry.price !== 'number' || Number.isNaN(entry.price)) {
		return fallback
	}

	return entry.price
}
